/**
 * Represents the global window object.
 */
export interface WindowObject extends Window {
    [key: string]: any;
    navigator: WindowNavigator;
    cseEncrypt?: CseEncrypt;
    nca3DSWebSDK?: Nca3DSWebSDK;
    pollOrderStatus?: (orderId: string) => void;
    showAuthenticationFrame?: () => HTMLIFrameElement;
    redirectForThreeDsOneAuthentication?: (url: string, postParameters: Record<string, any>) => void;
    ApplePaySession: ApplePaySession;
}

interface WindowNavigator extends Navigator {
    userLanguage?: string;
    userAgentData?: any; // TODO: Specify type
}

/**
 * Get the window object, which allows adding of variables without TypeScript errors.
 */
export function getWindowObject(): WindowObject {
    return window;
}

/**
 * Get the url query parameters and return them as an object.
 */
export function getQueryParams(location: WindowObject | string): URLSearchParams {
    // For the redirectToMyBTC method
    if (typeof location === 'string') {
        const url = new URL(location);
        return new URLSearchParams(url.search);
    }

    return new URLSearchParams(location.location.search);
}

/**
 * Remove query parameters from the url, without reloading the page.
 */
export function removeQueryParamsFromWindow(...remove: string[]): void {
    const window = getWindowObject();
    const params = getQueryParams(window);

    remove.forEach((param: string) => params.delete(param));

    if (params.size === 0) {
        window.history.replaceState(null, '', window.location.pathname);
        return;
    }

    window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
}

/**
 * Open a url in a new tab or the same tab.
 * If opening a new tab, focus on it.
 */
export function openUrl(url: string, target: 'self' | 'blank' = 'self'): void {
    const window = getWindowObject();

    target === 'blank' ? window.open(url, '_blank')?.focus() : window.open(url, '_self');
}

type CseEncrypt = (cardHolderName: string, cardNumber: string, expiryMonth: string, expiryYear: string, securityCode: string) => string;

type Nca3DSWebSDK = {
    createIframeAndInit3DSMethod: (
        url: string,
        data: string,
        frameName: string,
        rootContainer: HTMLElement,
        onFrameLoadCallback?: () => void
    ) => HTMLIFrameElement;
    init3DSMethod: (
        url: string,
        data: string,
        onFrameLoadCallback?: () => void
    ) => HTMLIFrameElement;
    init3DSChallengeRequest: (
        url: string,
        data: string,
        container: HTMLElement,
    ) => void;
    createIFrameAndInit3DSChallengeRequest: (
        url: string,
        data: string,
        container: HTMLElement,
        onFrameLoadCallback?: () => void
    ) => HTMLIFrameElement;
};